import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Camera } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { profileCountries, profileCities, nearCountries } from "@/lib/places";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { AddressInput } from "@/components/AddressFields";

export default function EditProfilePage() {
  const nav = useNavigate();
  const { user, setUser, setAuthOpen, errMsg } = useAuth();
  const [form, setForm] = useState(null);
  const [avatarPrev, setAvatarPrev] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (user === false) setAuthOpen(true); }, [user, setAuthOpen]);
  useEffect(() => {
    if (user && user !== false) {
      setForm({
        name: user.name || "", surname: user.surname || "", nickname: user.nickname || "", email: user.email || "",
        country: user.country || "", city: user.city || "", address: user.address || "",
        bio: user.bio || "", avatar: user.avatar || "", discoverable: user.discoverable !== false,
      });
      setAvatarPrev(user.avatar || "");
    }
  }, [user]);

  if (!user || user === false || !form) return <div className="p-8 text-center text-slate-500">Kyçu për të ndryshuar profilin.</div>;

  const pickAvatar = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setAvatarPrev(URL.createObjectURL(f));
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((s) => ({ ...s, avatar: r.data.url }));
    } catch (e2) { toast.error(errMsg(e2)); }
    finally { setUploading(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.put("/auth/me", form);
      setUser(r.data);
      toast.success("Profili u ruajt");
      nav("/profil");
    } catch (e2) { toast.error(errMsg(e2)); }
    finally { setSaving(false); }
  };

  const cities = profileCities(form.country);
  const inp = "rounded-xl h-11";
  const sel = `${inp} w-full border border-slate-200 rounded-xl px-3 bg-white text-sm`;

  return (
    <div className="gs-fade px-4 pt-4 pb-6" data-testid="edit-profile-page">
      <button onClick={() => nav("/profil")} className="flex items-center gap-1 text-sm text-slate-500 mb-2"><ArrowLeft className="w-4 h-4" />Profili</button>
      <h1 className="text-2xl font-extrabold text-slate-900 mb-4">Ndrysho profilin</h1>

      <form onSubmit={save} className="space-y-3">
        <div className="flex justify-center items-center gap-3 mb-2">
          {["male", "female"].map((g) => (
            <button key={g} type="button" data-testid={`edit-avatar-${g}`} onClick={() => { setForm((s) => ({ ...s, avatar: `/avatars/${g}.png` })); setAvatarPrev(`/avatars/${g}.png`); }} className={`w-16 h-16 rounded-full overflow-hidden transition-transform ${form.avatar === `/avatars/${g}.png` ? "ring-4 ring-emerald-500 scale-110" : "opacity-70"}`}>
              <img src={`/avatars/${g}.png`} alt={g === "male" ? "Burrë" : "Grua"} className="w-full h-full object-cover" />
            </button>
          ))}
          <label className="relative cursor-pointer" data-testid="edit-avatar-label">
            <div className="w-24 h-24 rounded-full bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden">
              {avatarPrev ? <img src={avatarPrev} alt="" className="w-full h-full object-cover" /> : <Camera className="w-7 h-7 text-slate-400" />}
            </div>
            <span className="absolute -bottom-1 -right-1 bg-emerald-500 text-white rounded-full p-1.5"><Camera className="w-4 h-4" /></span>
            <input data-testid="edit-avatar-input" type="file" accept="image/*" onChange={pickAvatar} className="hidden" />
          </label>
        </div>
        {uploading && <p className="text-xs text-center text-slate-400">Duke ngarkuar foton...</p>}

        <div className="grid grid-cols-2 gap-2">
          <div><label className="text-xs text-slate-500 font-medium">Emri</label><Input data-testid="edit-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className={inp} /></div>
          <div><label className="text-xs text-slate-500 font-medium">Mbiemri</label><Input data-testid="edit-surname" value={form.surname} onChange={(e) => setForm({ ...form, surname: e.target.value })} className={inp} /></div>
        </div>
        <div>
          <label className="text-xs text-slate-500 font-medium">Nofka (opsionale)</label>
          <Input data-testid="edit-nickname" placeholder="p.sh. Ari" value={form.nickname || ""} onChange={(e) => setForm({ ...form, nickname: e.target.value })} className={inp} />
          <p className="text-[11px] text-slate-400 mt-1">Të tjerët shohin vetëm nofkën. Pa nofkë shfaqet vetëm emri, kurrë mbiemri.</p>
        </div>
        <div><label className="text-xs text-slate-500 font-medium">Email</label><Input data-testid="edit-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className={inp} /></div>
        <div><label className="text-xs text-slate-500 font-medium">Shteti</label>
          <select data-testid="edit-country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value, city: "" })} className={sel}>
            <option value="">Zgjedh shtetin</option>
            {nearCountries(profileCountries()).map((c) => <option key={c.name} value={c.name}>{c.flag} {c.name}</option>)}
          </select>
        </div>
        <div><label className="text-xs text-slate-500 font-medium">Qyteti</label>
          <select data-testid="edit-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} disabled={!form.country} className={`${sel} disabled:opacity-50`}>
            <option value="">{form.country ? "Zgjedh qytetin" : "Zgjedh shtetin më parë"}</option>
            {cities.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div><label className="text-xs text-slate-500 font-medium">Adresa</label><AddressInput testid="edit-address" f={form} setF={setForm} noPostal placeholder="Rruga, numri" className={`w-full border border-slate-200 px-3 text-sm ${inp}`} /></div>
        <div><label className="text-xs text-slate-500 font-medium">Përshkrimi</label><Textarea data-testid="edit-bio" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={3} className="rounded-xl" placeholder="Shkruaj diçka për veten..." /></div>

        <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer bg-slate-50 rounded-xl p-3">
          <input data-testid="edit-discoverable" type="checkbox" checked={form.discoverable} onChange={(e) => setForm({ ...form, discoverable: e.target.checked })} className="mt-0.5 w-4 h-4 accent-emerald-500" />
          <span>Shfaqu te "Kërko persona"<span className="block text-xs text-slate-400">Nëse e çaktivizon, nuk do të shfaqesh në kërkimin publik të personave.</span></span>
        </label>

        <Button data-testid="save-profile-btn" type="submit" disabled={saving || uploading} className="w-full h-12 rounded-xl bg-emerald-500 hover:bg-emerald-600 font-semibold">{saving ? "Duke ruajtur..." : "Ruaj ndryshimet"}</Button>
      </form>
    </div>
  );
}
